/**
 * The fleet at this minute, from Tractor State: how many are production
 * ready, who is not and since when, who has no mark. Activity is not here —
 * the lake lags hours — only the time it has reached.
 */
import React from "react";
import { styled } from "styled-components";
import { clockText, primaryReason, reasonLabel, sinceText } from "./labels";
import { FleetResponse } from "./types";
import { tokens } from "../TractorState/tokens";

interface RightNowProps {
  fleet: FleetResponse;
}

const RightNow: React.FC<RightNowProps> = ({ fleet }) => {
  const names = new Map(fleet.vehicles.map((v) => [v.vehicle, v.display_name]));
  const through = fleet.lake_through ? clockText(fleet.lake_through) : null;
  return (
    <Strip>
      <Eyebrow>Right now · {clockText(fleet.now.at)}</Eyebrow>
      <Headline>
        {fleet.now.ready} of {fleet.vehicles.length} production ready
      </Headline>
      {fleet.now.not_ready.map((entry) => {
        const reason = primaryReason(entry.reasons);
        const since = sinceText(entry.since);
        return (
          <Chip key={entry.vehicle} $tone="bad" title={entry.note ?? undefined}>
            <Dot $tone="bad" />
            {entry.display_name}
            {reason ? ` · ${reasonLabel(reason)}` : ""}
            {since ? ` · ${since}` : ""}
          </Chip>
        );
      })}
      {fleet.now.unset.map((vehicle) => (
        <Chip key={vehicle} $tone="muted">
          <Dot $tone="muted" />
          {names.get(vehicle) ?? vehicle.toUpperCase()} · no readiness mark
        </Chip>
      ))}
      <Spacer />
      <Sub>{through ? `Lake data through ${through}` : "No lake data yet"}</Sub>
    </Strip>
  );
};

export default RightNow;

const Strip = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 14px;
  padding: 10px 14px;
  background: ${tokens.white};
  border: 1px solid ${tokens.line};
  border-radius: 10px;
`;

const Eyebrow = styled.span`
  font: 500 11px ${tokens.fontMono};
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${tokens.slate};
`;

const Headline = styled.strong`
  font: 600 14px ${tokens.fontSans};
  color: ${tokens.ink};
`;

const Chip = styled.span<{ $tone: "bad" | "muted" }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 8px;
  border-radius: 999px;
  background: ${tokens.offWhite};
  font: 500 11px ${tokens.fontMono};
  color: ${(p) => (p.$tone === "bad" ? tokens.groundedText : tokens.slate)};
  white-space: nowrap;
`;

const Dot = styled.span<{ $tone: "bad" | "muted" }>`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: ${(p) =>
    p.$tone === "bad" ? tokens.groundedText : tokens.offlineDot};
`;

const Spacer = styled.span`
  flex: 1;
`;

const Sub = styled.span`
  font: 12px ${tokens.fontSans};
  color: ${tokens.slate};
`;
